/**
 * Helpers para jerarquía N:N (parent_ids + parent_id BC).
 * parent_id en API = primer elemento de parent_ids.
 * El grafo puede ser dinámico (rombo, varios padres); no asumir árbol lineal.
 */
export type HierarchyParentNode = {
	parent_id?: string | null;
	parent_ids?: string[] | null;
};

export type HierarchyIdentifiable = HierarchyParentNode & {
	id?: string | number | null;
	code?: string | number | null;
};

export function allParentIds(node?: HierarchyParentNode | null): string[] {
	if (!node) {
		return [];
	}
	if (Array.isArray(node.parent_ids) && node.parent_ids.length > 0) {
		return node.parent_ids.map(String);
	}
	return node.parent_id ? [String(node.parent_id)] : [];
}

export function primaryParentId(node?: HierarchyParentNode | null): string | null {
	const ids = allParentIds(node);
	return ids[0] ?? null;
}

export function hasParentId(node: HierarchyParentNode | null | undefined, parentId: string): boolean {
	if (!parentId) {
		return false;
	}
	return allParentIds(node).includes(String(parentId));
}

export function isHierarchyRoot(node?: HierarchyParentNode | null): boolean {
	return allParentIds(node).length === 0;
}

export function hierarchyNodeId(node?: HierarchyIdentifiable | null): string | null {
	if (!node) {
		return null;
	}
	if (node.id != null && node.id !== '') {
		return String(node.id);
	}
	if (node.code != null && node.code !== '') {
		return String(node.code);
	}
	return null;
}

/** Hijos directos: cualquier nodo cuyo parent_ids incluya parentId. */
export function childrenOf<T extends HierarchyParentNode>(
	parentId: string,
	nodes: T[],
): T[] {
	if (!parentId) {
		return [];
	}
	return nodes.filter((n) => hasParentId(n, parentId));
}

/**
 * Todos los ancestros de un nodo (BFS por cualquier padre), sin el propio id.
 * `nodes` indexados por id/code.
 */
export function allAncestorIds(
	startId: string,
	nodes: HierarchyIdentifiable[],
): string[] {
	const byId = new Map<string, HierarchyIdentifiable>();
	for (const n of nodes) {
		const id = hierarchyNodeId(n);
		if (id) {
			byId.set(id, n);
		}
	}
	const result: string[] = [];
	const seen = new Set<string>([String(startId)]);
	const queue = [...allParentIds(byId.get(String(startId)))];
	while (queue.length) {
		const id = queue.shift()!;
		if (seen.has(id)) {
			continue;
		}
		seen.add(id);
		result.push(id);
		for (const p of allParentIds(byId.get(id))) {
			if (!seen.has(p)) {
				queue.push(p);
			}
		}
	}
	return result;
}

/**
 * Ancestros de un nivel ordenados por profundidad (raíz primero).
 * Incluye todas las ramas del rombo (no solo padre principal).
 */
export function ancestorLevelsByDepth(
	levelId: string,
	levels: HierarchyIdentifiable[],
): {id: string; depth: number}[] {
	const byId = new Map<string, HierarchyIdentifiable>();
	for (const l of levels) {
		const id = hierarchyNodeId(l);
		if (id) {
			byId.set(id, l);
		}
	}
	const depth = new Map<string, number>();
	const queue: string[] = [];
	for (const p of allParentIds(byId.get(String(levelId)))) {
		depth.set(p, 0);
		queue.push(p);
	}
	while (queue.length) {
		const id = queue.shift()!;
		const d = depth.get(id) ?? 0;
		for (const p of allParentIds(byId.get(id))) {
			const next = d + 1;
			if (!depth.has(p) || next > (depth.get(p) ?? 0)) {
				depth.set(p, next);
				queue.push(p);
			}
		}
	}
	// depth 0 = padre directo; invertimos para raíz primero
	const max = Math.max(0, ...depth.values());
	return [...depth.entries()]
		.map(([id, d]) => ({id, depth: max - d}))
		.sort((a, b) => a.depth - b.depth || a.id.localeCompare(b.id));
}

/** Une labels de padres; vacío → '-'. */
export function joinParentLabels(
	ids: string[],
	resolveLabel: (id: string) => string | null | undefined,
	empty = '-',
): string {
	if (!ids.length) {
		return empty;
	}
	const labels = ids.map((id) => resolveLabel(id)).filter((l): l is string => !!l);
	return labels.length ? labels.join(', ') : empty;
}
